import {
  ResponseContextMenuResponse,
  ResponseContextOption,
  ResponseContextState,
} from "../types/realtimeSpeech";

const DEFAULT_OPTION_ID = "default";

const findOption = (
  menu: ResponseContextMenuResponse | null,
  fieldKey: "persona" | "engagement_level",
  id: string | null,
): ResponseContextOption | null => {
  if (!id) return null;
  const field = menu?.fields.find((f) => f.key === fieldKey);
  return field?.options.find((o) => o.id === id) ?? null;
};

const getDefaultPersonaOption = (
  menu: ResponseContextMenuResponse | null,
): ResponseContextOption | null => {
  const field = menu?.fields.find((f) => f.key === "persona");
  return (
    field?.options.find((o) => o.is_default || o.id === DEFAULT_OPTION_ID) ??
    null
  );
};

//Builds full response_context from selected ids + menu (for POST / local state).
export const buildResponseContextState = (
  ids: {
    persona_id?: string | null;
    engagement_level_id?: string | null;
  },
  current: ResponseContextState,
  menu: ResponseContextMenuResponse | null,
): ResponseContextState => {
  const personaId =
    ids.persona_id !== undefined ? ids.persona_id : current.persona_id;
  const personaChanged = ids.persona_id !== undefined;
  const engagementLevelId =
    ids.engagement_level_id !== undefined
      ? ids.engagement_level_id
      : personaChanged
        ? null
        : current.engagement_level_id;

  const resolvedPersonaId =
    personaId && personaId !== DEFAULT_OPTION_ID ? personaId : null;
  const resolvedEngagementId =
    engagementLevelId && engagementLevelId !== DEFAULT_OPTION_ID
      ? engagementLevelId
      : null;

  const personaOption = findOption(menu, "persona", resolvedPersonaId);
  const engagementOption = findOption(
    menu,
    "engagement_level",
    resolvedEngagementId,
  );

  const persona =
    resolvedPersonaId && personaOption
      ? {
          id: personaOption.id,
          text: personaOption.text,
          description: personaOption.description,
          avatar: personaOption.avatar,
          display_name: personaOption.display_name,
        }
      : null;

  const engagement_level =
    resolvedEngagementId && engagementOption
      ? {
          id: engagementOption.id,
          text: engagementOption.text,
          description: engagementOption.description,
        }
      : null;

  const is_default = !resolvedPersonaId && !resolvedEngagementId;
  const defaultOption = getDefaultPersonaOption(menu);

  // Label on the chip is persona-driven: Default when no persona, even if engagement is set.
  const display_name = !resolvedPersonaId
    ? (defaultOption?.display_name ?? current.display_name ?? "Default")
    : (persona?.display_name ?? null);

  return {
    persona_id: resolvedPersonaId,
    engagement_level_id: resolvedEngagementId,
    persona,
    engagement_level,
    is_default,
    display_name,
  };
};

export const isResponseContextState = (
  value: unknown,
): value is ResponseContextState => {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    ("persona_id" in obj || "engagement_level_id" in obj) &&
    ("is_default" in obj || "persona" in obj || "engagement_level" in obj)
  );
};

export const getPersonaLabel = (
  responseContext: ResponseContextState,
): string | null => responseContext.persona?.text?.trim() || null;

//Chip right-side label + fallback when backend omits display_name.
export const getPersonaDisplayName = (
  responseContext: ResponseContextState,
  menu: ResponseContextMenuResponse | null,
): string | null => {
  const fromContext = responseContext.display_name?.trim();
  if (fromContext) return fromContext;

  const fromPersona = responseContext.persona?.display_name?.trim();
  if (fromPersona) return fromPersona;

  if (!responseContext.persona_id) {
    return getDefaultPersonaOption(menu)?.display_name?.trim() || "Default";
  }

  return (
    findOption(menu, "persona", responseContext.persona_id)?.display_name?.trim() ||
    null
  );
};

//Tooltip text + fallback to menu / default option.
export const getPersonaDescription = (
  responseContext: ResponseContextState,
  menu: ResponseContextMenuResponse | null,
): string | null => {
  const fromPersona = responseContext.persona?.description?.trim();
  if (fromPersona) return fromPersona;

  if (!responseContext.persona_id) {
    return getDefaultPersonaOption(menu)?.description?.trim() || null;
  }

  return (
    findOption(menu, "persona", responseContext.persona_id)?.description?.trim() ||
    null
  );
};
